import { Stack } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import {
  type Control,
  Controller,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { DatePickerWithEventTracking } from '@/components/DatePicker/DatePickerV2';

interface CustomDateRangeFields {
  customDateStart?: string | null;
  customDateEnd?: string | null;
}

function parseDateField(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

function formatOrClear(date: Date | null): string | null {
  return date && isValid(date) ? format(date, 'yyyy-MM-dd') : null;
}

export function CustomDateRangePickers<T extends CustomDateRangeFields>({
  control,
  setValue,
  eventContainerPrefix,
  required,
  minDate,
}: {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  eventContainerPrefix: string;
  required: boolean;
  minDate?: string;
}) {
  const startValue = useWatch({
    control,
    name: 'customDateStart' as never,
  });
  const minDateParsed: Date | undefined = parseDateField(minDate) ?? undefined;
  const endMinDate =
    parseDateField(startValue as string | undefined) ?? minDateParsed;

  return (
    <Stack sx={{ pl: 6.25, gap: 2 }}>
      <Controller
        name={'customDateStart' as never}
        control={control}
        rules={{ required: required ? 'Required' : false }}
        render={({ field, fieldState }) => (
          <DatePickerWithEventTracking
            eventContainer={`${eventContainerPrefix}-custom-start-date`}
            value={parseDateField(field.value as string)}
            minDate={minDateParsed}
            onChange={(date) => {
              field.onChange(formatOrClear(date));
              setValue('customDateEnd' as never, null as never, {
                shouldDirty: true,
              });
            }}
            slotProps={{
              textField: {
                label: 'Start Date',
                required: true,
                fullWidth: true,
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
              },
            }}
          />
        )}
      />
      <Controller
        name={'customDateEnd' as never}
        control={control}
        rules={{ required: required ? 'Required' : false }}
        render={({ field, fieldState }) => (
          <DatePickerWithEventTracking
            eventContainer={`${eventContainerPrefix}-custom-end-date`}
            value={parseDateField(field.value as string)}
            minDate={endMinDate}
            onChange={(date) => field.onChange(formatOrClear(date))}
            slotProps={{
              textField: {
                label: 'End Date',
                required: true,
                fullWidth: true,
                error: !!fieldState.error,
                helperText: fieldState.error?.message,
              },
            }}
          />
        )}
      />
    </Stack>
  );
}
